'use strict';
/* ============================================================================
 * tcg-model.js — pure, DOM-free logic for the TCG set/rarity filter.
 * UMD: `require()` in Node (tests) or `window.TCGModel` in the theme. The DOM
 * wrapper (tcg-filter.js, Phase 3) drives this; keeping it pure keeps it tested.
 *
 * item shape: { id, title, game, era, set, rarity, format, year, release?, mode? }
 *   release: ISO 'YYYY-MM-DD' (or 'YYYY') set-release date — drives set ordering.
 * state shape: { <axisKey>: Set<value> }   config: { axes:[...], shelfCap, shelfCapMin }
 * ========================================================================== */
(function (root, factory) {
  if (typeof module !== 'undefined' && module.exports) module.exports = factory();
  else root.TCGModel = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  // Canonical rarity ladders — rank = index (ascending = rarer). The rarity
  // facet DISPLAYS rarest-first, and per owner 'Promo' is the top premium tier
  // (last in the array). Mirrors tcg/filter/build-phase0.js (single source).
  var LADDERS = {
    'Pokemon': ['Common', 'Uncommon', 'Rare', 'Rare Holo', 'Rare Holo V', 'Radiant Rare',
      'Rare Holo VMAX', 'Rare Holo VSTAR', 'Amazing Rare', 'Trainer Gallery', 'Double Rare',
      'ACE SPEC Rare', 'Full Art', 'Ultra Rare', 'Shiny Rare', 'Illustration Rare',
      'Rainbow Rare', 'Special Illustration Rare', 'Gold Secret Rare', 'Secret Rare', 'Hyper Rare', 'Promo'],
    'Magic: The Gathering': ['Common', 'Uncommon', 'Rare', 'Mythic Rare', 'Special', 'Serialized'],
    'Yu-Gi-Oh': ['Common', 'Rare', 'Super Rare', 'Ultra Rare', 'Secret Rare', 'Ultimate Rare',
      "Collector's Rare", 'Ghost Rare', 'Starlight Rare', 'Quarter Century Secret Rare', 'Prismatic Secret Rare'],
    'One Piece': ['Common', 'C', 'Uncommon', 'UC', 'Rare', 'R', 'Leader', 'L', 'Super Rare', 'SR',
      'Special', 'SP', 'Secret Rare', 'SEC', 'Manga Rare'],
    'Lorcana': ['Common', 'Uncommon', 'Rare', 'Super Rare', 'Legendary', 'Epic', 'Enchanted', 'Iconic'],
    'Digimon': ['Common', 'C', 'Uncommon', 'U', 'Rare', 'R', 'Super Rare', 'SR', 'Secret Rare', 'SEC'],
  };
  var SINGLE_FORMATS = { 'Single': 1, 'Graded Single': 1, 'Raw Single': 1 };
  var GRADED_FORMATS = { 'Graded Single': 1 };
  // Graded-slab signal in a title (fallback when format is just 'Single').
  var GRADE_RE = /\b(?:PSA|BGS|CGC|SGC|TAG|ACE|GMA|HGA)\b\s*\d|\bgem\s*mint\b|\bgraded\b/i;
  var UNKNOWN_RANK = 900;
  // Era display order (newest generation first = top of the page). Unknown eras
  // sink to the bottom, alpha-sorted. Pokémon is the only multi-era game that
  // matters here; others have 1–2 eras where order is immaterial.
  var ERA_ORDER = ['Mega Evolution', 'Scarlet & Violet', 'Sword & Shield', 'Sun & Moon', 'XY',
    'Black & White', 'HeartGold SoulSilver', 'Platinum', 'Diamond & Pearl', 'EX', 'e-Card', 'Neo', 'Base'];
  function eraRank(era) { var i = ERA_ORDER.indexOf(era); return i === -1 ? UNKNOWN_RANK : i; }
  // Per-page override (the section's era_order setting, via config JSON) so the
  // same model serves Magic, Lorcana, etc. without a rebuild.
  function setEraOrder(list) {
    if (Object.prototype.toString.call(list) === '[object Array]' && list.length) ERA_ORDER = list.slice();
  }

  function lc(s) { return String(s == null ? '' : s).toLowerCase(); }

  function rarityRank(game, rarity) {
    var lad = LADDERS[game];
    if (!lad || !rarity) return UNKNOWN_RANK;
    for (var i = 0; i < lad.length; i++) if (lc(lad[i]) === lc(rarity)) return i;
    return UNKNOWN_RANK;
  }

  // Modes: 'sealed' | 'slab' (graded single) | 'single' (raw single) | 'other' (accessory).
  function classifyMode(it) {
    if (it.game === 'Accessory' || it.format === 'Accessory') return 'other';
    var isSingle = SINGLE_FORMATS[it.format] || it.rarity;
    if (!isSingle) return 'sealed';
    if (GRADED_FORMATS[it.format] || GRADE_RE.test(it.title || '')) return 'slab';
    return 'single';
  }
  function modeOf(it) { return it.mode || classifyMode(it); }
  // appliesTo may be a mode string or an array of modes.
  function appliesToMode(at, mode) { return Array.isArray(at) ? at.indexOf(mode) !== -1 : at === mode; }

  // rarest-first display order
  function orderRarities(game, arr) {
    return arr.slice().sort(function (a, b) { return rarityRank(game, b) - rarityRank(game, a); });
  }

  // Hide the Game facet when the collection is effectively one game (e.g. the
  // Pokemon page). Blank/unknown game values are ignored — they aren't real games
  // and the option list already excludes them, so a lone stray blank must not keep
  // a redundant single-option "Game" pod on show.
  function gameFacetHidden(items) {
    var seen = {};
    for (var i = 0; i < items.length; i++) { var g = items[i].game; if (g !== '' && g != null) seen[g] = 1; }
    return Object.keys(seen).length <= 1;
  }

  // OR within an axis (Set membership), AND across axes. An axis with
  // appliesTo only constrains items of that mode (so a rarity filter narrows
  // singles but does NOT hide sealed product, which has no rarity).
  function matches(it, state, cfg) {
    var mode = modeOf(it);
    for (var a = 0; a < cfg.axes.length; a++) {
      var ax = cfg.axes[a], sel = state[ax.key];
      if (!sel || !sel.size) continue;
      if (ax.appliesTo && !appliesToMode(ax.appliesTo, mode)) continue;
      if (!sel.has(it[ax.key])) return false;
    }
    return true;
  }

  // Reactive counts: each axis value is counted against the OTHER axes' active
  // filters (so counts show what selecting that value would yield).
  function facetCounts(items, state, cfg) {
    var out = {};
    cfg.axes.forEach(function (ax) { out[ax.key] = {}; });
    for (var i = 0; i < items.length; i++) {
      var it = items[i], mode = modeOf(it);
      for (var a = 0; a < cfg.axes.length; a++) {
        var ax = cfg.axes[a];
        if (ax.appliesTo && !appliesToMode(ax.appliesTo, mode)) continue;
        var ok = true;
        for (var b = 0; b < cfg.axes.length; b++) {
          if (b === a) continue;
          var bx = cfg.axes[b], sel = state[bx.key];
          if (bx.appliesTo && !appliesToMode(bx.appliesTo, mode)) continue;
          if (sel && sel.size && !sel.has(it[bx.key])) { ok = false; break; }
        }
        if (!ok) continue;
        var v = it[ax.key];
        if (v === '' || v == null) continue;
        out[ax.key][v] = (out[ax.key][v] || 0) + 1;
      }
    }
    return out;
  }

  function byStr(a, b) { return a < b ? -1 : (a > b ? 1 : 0); }
  function relOf(it) { return it && it.release ? String(it.release) : ''; }
  // Descending release-date compare (ISO 'YYYY-MM-DD' or 'YYYY'); newest first,
  // unknown ('') sinks to the bottom. Lexicographic order == chronological for ISO.
  function cmpRelDesc(a, b) { return a === b ? 0 : (a < b ? 1 : -1); }
  // Singles & slabs share ordering: rarest-first, then newest-release, then set/title.
  function sortSingleLike(arr) {
    return arr.sort(function (a, b) {
      return (rarityRank(b.game, b.rarity) - rarityRank(a.game, a.rarity))
        || cmpRelDesc(relOf(a), relOf(b)) || byStr(a.set, b.set) || byStr(a.title, b.title);
    });
  }

  // Two-mode, era-primary plan:
  //   SEALED → era bands (newest first). Within an era, a set with ≥ setShelfMin
  //            items gets its own preview-capped shelf; smaller sets fold into
  //            the era's `loose` grid.
  //   SINGLES → one rarity-ordered list (rarest-first, tie-break set→title).
  //   OTHER   → accessories bucket at the bottom.
  function computePlan(items, state, cfg) {
    var matched = items.filter(function (it) { return matches(it, state, cfg); });
    var eraMap = {}, singles = [], slabs = [], other = [];
    for (var i = 0; i < matched.length; i++) {
      var it = matched[i], mode = modeOf(it);
      if (mode === 'other') { other.push(it); continue; }
      if (mode === 'slab') { slabs.push(it); continue; }
      if (mode === 'single') { singles.push(it); continue; }
      var era = it.era || '', set = it.set || '~';
      var em = eraMap[era] || (eraMap[era] = {});
      (em[set] || (em[set] = [])).push(it);
    }
    var setShelfMin = cfg.setShelfMin || 3;
    var eras = Object.keys(eraMap)
      .sort(function (a, b) { return (eraRank(a) - eraRank(b)) || byStr(a, b); })
      .map(function (era) {
        var sets = eraMap[era], shelves = [], loose = [], total = 0;
        // Order sets newest-release → oldest (by the set's most recent printing),
        // tie-break alphabetically. Items within a set + the loose grid follow suit.
        var setKeys = Object.keys(sets), setRel = {};
        setKeys.forEach(function (s) {
          // Set sort key = the MOST COMMON release among the set's items (robust to
          // a single mis-dated card); ties break to the newer date.
          var a = sets[s], freq = {}, best = '', bestN = 0;
          for (var k = 0; k < a.length; k++) {
            var r = relOf(a[k]); if (!r) continue;
            freq[r] = (freq[r] || 0) + 1;
            if (freq[r] > bestN || (freq[r] === bestN && r > best)) { bestN = freq[r]; best = r; }
          }
          setRel[s] = best;
        });
        setKeys.sort(function (a, b) { return cmpRelDesc(setRel[a], setRel[b]) || byStr(a, b); });
        setKeys.forEach(function (set) {
          var arr = sets[set].slice().sort(function (x, y) { return cmpRelDesc(relOf(x), relOf(y)) || byStr(x.title, y.title); });
          total += arr.length;
          if (arr.length >= setShelfMin) {
            var full = arr.length < cfg.shelfCapMin;
            shelves.push({ set: set === '~' ? '' : set, key: era + '|' + set, items: arr,
              total: arr.length, shown: full ? arr.slice() : arr.slice(0, cfg.shelfCap) });
          } else { loose = loose.concat(arr); }
        });
        loose.sort(function (x, y) { return cmpRelDesc(relOf(x), relOf(y)) || byStr(x.title, y.title); });
        return { era: era, key: era || '~', shelves: shelves, loose: loose, total: total };
      });
    // Singles & slabs: rarest-first, then newest-release, then set/title.
    sortSingleLike(singles); sortSingleLike(slabs);
    var ids = function (arr) { return arr.map(function (x) { return x.id; }).join(','); };
    var sig = eras.map(function (e) {
      return e.key + ':' + e.shelves.map(function (s) { return s.key + '[' + ids(s.shown) + '+' + s.total + ']'; }).join(';') + '/L' + ids(e.loose);
    }).join('|') + '#S' + ids(singles) + '#G' + ids(slabs) + '#O' + ids(other);
    return { eras: eras, singles: singles, slabs: slabs, other: other, sig: sig, matchedCount: matched.length };
  }

  return {
    LADDERS: LADDERS, SINGLE_FORMATS: SINGLE_FORMATS, UNKNOWN_RANK: UNKNOWN_RANK,
    rarityRank: rarityRank, eraRank: eraRank, setEraOrder: setEraOrder, classifyMode: classifyMode, orderRarities: orderRarities,
    gameFacetHidden: gameFacetHidden, matches: matches, facetCounts: facetCounts, computePlan: computePlan,
  };
});
